/**
 * System Prompt REST API client
 *
 * Auth: Chainlit sets an httponly cookie (`access_token`) on login.
 * Same-origin fetch with credentials:'include' sends it automatically.
 *
 * 後端規劃文件：docs/SYSTEM_PROMPT_FEATURE_PLAN.md
 */

const BASE = '/api/system-prompts';

const req = (method: string, path: string, body?: unknown) =>
  fetch(`${BASE}${path}`, {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  }).then(async (res) => {
    if (!res.ok) {
      const text = await res.text().catch(() => res.statusText);
      throw new Error(text || `HTTP ${res.status}`);
    }
    if (res.status === 204) return undefined;
    return res.json();
  });

export interface SystemPrompt {
  id: string;
  org_id: string;
  kb_id: string;
  name: string;
  content: string;
  description: string;
  is_active: boolean;
  is_default: boolean;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface SystemPromptAudit {
  id: string;
  prompt_id: string | null;
  org_id: string | null;
  kb_id: string | null;
  /** 'create' | 'update' | 'delete' */
  action: string;
  actor_user_id: string | null;
  actor_username: string | null;
  old_name: string | null;
  new_name: string | null;
  old_content: string | null;
  new_content: string | null;
  diff_summary: string | null;
  created_at: string;
}

export interface CreateSystemPromptInput {
  kb_id: string;
  name: string;
  content: string;
  description?: string;
  is_default?: boolean;
  /** super_admin 建立時必填；org_user 由後端自動帶入自家 org */
  org_id?: string;
}

export interface UpdateSystemPromptInput {
  name?: string;
  content?: string;
  description?: string;
  is_active?: boolean;
  is_default?: boolean;
}

const qs = (params: Record<string, string | boolean | number | undefined>) => {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== '') sp.append(k, String(v));
  });
  const s = sp.toString();
  return s ? `?${s}` : '';
};

export const systemPromptsApi = {
  /** 列出某 KB 可見的 Prompt（公司隔離 + is_active 過濾） */
  list: (kbId: string, opts?: { includeInactive?: boolean; orgId?: string }): Promise<SystemPrompt[]> =>
    req('GET', qs({ kb_id: kbId, include_inactive: opts?.includeInactive, org_id: opts?.orgId })),

  /** 智慧載入：回傳該使用者在此 KB 應預設帶入的 Prompt（或 null） */
  resolve: (kbId: string, orgId?: string): Promise<{ prompt: SystemPrompt | null }> =>
    req('GET', `/resolve${qs({ kb_id: kbId, org_id: orgId })}`),

  /** 記錄使用者在某 KB 的「上次使用 Prompt」（prompt_id 可為 null 表清除） */
  setUsage: (kbId: string, promptId: string | null): Promise<{ kb_id: string; prompt_id: string | null }> =>
    req('PUT', '/usage', { kb_id: kbId, prompt_id: promptId }),

  /** 稽核日誌（公司/KB/操作人/時間/新舊內容 diff） */
  audit: (filter?: {
    kbId?: string;
    promptId?: string;
    orgId?: string;
    limit?: number;
    offset?: number;
  }): Promise<SystemPromptAudit[]> =>
    req('GET', `/audit${qs({
      kb_id: filter?.kbId,
      prompt_id: filter?.promptId,
      org_id: filter?.orgId,
      limit: filter?.limit,
      offset: filter?.offset,
    })}`),

  get: (id: string): Promise<SystemPrompt> => req('GET', `/${id}`),

  create: (data: CreateSystemPromptInput): Promise<SystemPrompt> => req('POST', '', data),

  update: (id: string, data: UpdateSystemPromptInput): Promise<SystemPrompt> =>
    req('PATCH', `/${id}`, data),

  delete: (id: string): Promise<void> => req('DELETE', `/${id}`),
};
